import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createWorkspace, updateWorkspace } from '../../services/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const WorkspaceModal = ({ isOpen, onClose, workspace = null }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const { addWorkspace, updateWorkspaceInState } = useWorkspace();

  const isEditMode = !!workspace;

  useEffect(() => {
    if (isOpen) {
      if (workspace) {
        setName(workspace.name || '');
        setSlug(workspace.slug || '');
      } else {
        setName('');
        setSlug('');
      }
      setErrors({});
      setSuccessMessage('');
    }
  }, [isOpen, workspace, isEditMode]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Workspace name is required';
    } else if (name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Only validate slug in create mode (not edit mode)
    if (!isEditMode && slug && slug.trim()) {
      if (!/^[a-z0-9-]+$/.test(slug.trim())) {
        newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
      }
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');
    
    try {
      if (isEditMode) {
        const workspaceId = workspace.id || workspace._id;
        
        const result = await updateWorkspace(workspaceId, name.trim());
        
        if (result.success) {
          updateWorkspaceInState(workspaceId, { name: name.trim() });
          setSuccessMessage('Workspace updated successfully!');
          setTimeout(() => onClose(), 500);
        } else {
          setErrors({ submit: result.error || 'Failed to update workspace' });
        }
      } else {
        const result = await createWorkspace(name.trim(), slug.trim() || null);
        
        if (result.success) {
          addWorkspace(result.data);
          setSuccessMessage('Workspace created successfully!');
          setTimeout(() => onClose(), 500);
        } else {
          setErrors({ submit: result.error || 'Failed to create workspace' });
        }
      }
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-bg-primary/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl shadow-2xl overflow-hidden bg-bg-secondary/95 border border-primary-500/15 shadow-[0_25px_50px_rgba(3,6,4,0.5),0_0_40px_rgba(50,205,50,0.1)]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-primary-500/10 bg-gradient-to-b from-primary-500/5 to-transparent">
          <h2 className="text-xl font-semibold text-text-primary">
            {isEditMode ? 'Edit Workspace' : 'Create New Workspace'}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-text-tertiary hover:text-primary-500 hover:bg-primary-500/10"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {/* Success Message */}
            {successMessage && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-success-50 border border-primary-500/30 text-primary-500">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>{successMessage}</span>
              </div>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-error-bg border border-error/30 text-red-400">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{errors.submit}</span>
              </div>
            )}

            {/* Workspace Name */}
            <div>
              <label htmlFor="workspace-name" className="block text-sm font-medium mb-2 text-text-secondary">
                Workspace Name <span className="text-error">*</span>
              </label>
              <input
                id="workspace-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
                placeholder="e.g., My Team, Acme Corp"
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-lg text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none bg-bg-input/80 text-text-primary focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/15 ${
                  errors.name ? 'border border-error' : 'border border-primary-500/15'
                }`}
                autoFocus
              />
              {errors.name && (
                <p className="mt-1.5 text-sm flex items-center gap-1 text-error">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Workspace Slug (optional, only for create) */}
            {!isEditMode && (
              <div>
                <label htmlFor="workspace-slug" className="block text-sm font-medium mb-2 text-text-secondary">
                  Workspace Slug <span className="text-xs text-text-tertiary">(Optional)</span>
                </label>
                <input
                  id="workspace-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value.toLowerCase()); setErrors(prev => ({ ...prev, slug: '' })); }}
                  placeholder="e.g., my-team, acme-corp"
                  disabled={loading}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm font-mono transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none bg-bg-input/80 text-text-primary focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/15 ${
                    errors.slug ? 'border border-error' : 'border border-primary-500/15'
                  }`}
                />
                {errors.slug && (
                  <p className="mt-1.5 text-sm flex items-center gap-1 text-error">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    {errors.slug}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-text-tertiary">
                  A unique identifier for your workspace. Auto-generated if left blank.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 rounded-b-xl border-t border-primary-500/10 bg-bg-primary/30">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-bg-elevated/80 text-text-secondary border border-primary-500/20 hover:bg-success-50 hover:border-primary-500/30"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-gradient-to-br from-primary-600 to-primary-500 text-black border-none shadow-glow-sm hover:from-primary-500 hover:to-green-400 hover:-translate-y-0.5 hover:shadow-glow"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
              )}
              {isEditMode ? 'Update Workspace' : 'Create Workspace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

WorkspaceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workspace: PropTypes.shape({
    id: PropTypes.string,
    _id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
};

export default WorkspaceModal;
